import React from "react"
import { css } from "@emotion/core"
import { graphql } from "gatsby"
import { MDXRenderer } from "gatsby-plugin-mdx"
import { MDXProvider } from "@mdx-js/react"

import { createPopper } from "@popperjs/core"

import Layout from "../components/layout"
import Ratings from "../components/ratings"
import TrackedLink from "../components/TrackedLink"
import Recommendation from "../components/recommendation"
import {
  PlaybookCategory,
  PlaybookContext,
  PlaybookProduct,
  PlaybookAlternatives,
} from "../components/playbook"

import { accurateFullArticleTime } from "../utils/utils.js"
import { calcTocWidth } from "../utils/post_utils.js"

import { Helmet } from "react-helmet"

import { rhythm } from "../utils/typography"
import TableOfContents from "../components/toc"
import SubscribeForm from "../components/subscribe_form"
import PopupSubscribeForm from "../components/popup_subscribe_form"
import Comments from "../components/comments"

const MAIN_CONTENT_ID = "main_content"
const SIDEBAR_CONTAINER_ID = "sidebar_container"

const WINDOW_MEDIA_TRANSITION_SIZE_PX = 576

class PostTemplate extends React.Component {
  constructor(props) {
    super(props)

    this.state = { tocWidth: "300px" }

    var that = this

    if (typeof window !== `undefined`) {
      let that = this
      window.onresize = function() {
        let toc_width = calcTocWidth(MAIN_CONTENT_ID, SIDEBAR_CONTAINER_ID)
        that.setState({ tocWidth: toc_width })
      }
    }
  }

  componentDidMount() {
    let toc_width = calcTocWidth(MAIN_CONTENT_ID, SIDEBAR_CONTAINER_ID)
    this.setState({ tocWidth: toc_width })

    let sidenote_numbers = document.getElementsByClassName("sidenote-number")
    let sidenotes = document.getElementsByClassName("sidenote")

    let popperInstance = null

    function create(i) {
      popperInstance = createPopper(sidenote_numbers[i], sidenotes[i], {
        modifiers: [
          {
            name: "offset",
            options: {
              offset: [0, 8],
            },
          },
        ],
      })
    }

    function destroy() {
      if (popperInstance) {
        popperInstance.destroy()
        popperInstance = null
      }
    }

    function show(i) {
      sidenotes[i].setAttribute("data-show", "")
      create(i)
    }

    function hide(i) {
      sidenotes[i].removeAttribute("data-show")
      destroy()

      // Popper leaves a margin: 0, which interferes with sidenote placement.
      // We have to explicitly remove it.
      sidenotes[i].style.margin = null
    }

    const showEvents = ["mouseenter", "focus", "touchstart"]
    const hideEvents = ["mouseleave", "blur"]

    showEvents.forEach(event => {
      for (let i = 0; i < sidenote_numbers.length; i++) {
        sidenote_numbers[i].addEventListener(event, function() {
          if (window.innerWidth <= WINDOW_MEDIA_TRANSITION_SIZE_PX) {
            show(i)
          }
        })
      }
    })

    hideEvents.forEach(event => {
      for (let i = 0; i < sidenote_numbers.length; i++) {
        sidenote_numbers[i].addEventListener(event, function() {
          if (window.innerWidth <= WINDOW_MEDIA_TRANSITION_SIZE_PX) {
            hide(i)
          }
        })
      }
    })
  }

  MaybeStubDisclaimer(frontmatter_stub) {
    if (frontmatter_stub == "true") {
      return (
        <update>
          <p>
            <p>
              <b
                css={css`
                  white-space: pre-wrap;
                `}
              >
                This is our response to an email from a reader.
              </b>
            </p>
            <p>
              With their permission, we have released their anonymized original
              email, as well as our response. We're doing this because we
              believe the information and insights contained in our responses
              can be more broadly helpful, especially as we're still working on
              the full-length guide. However, do note that this recommendation
              will be rougher and less in-depth than our full-length guides, and
              that some of our guidance might change as we complete our research
              on a topic.
            </p>
            <p>
              If you want a recommendation on a category that we haven't yet
              posted about, don't hesitate to{" "}
              <a href="/contact">shoot us an email</a>.
            </p>
          </p>
        </update>
      )
    }
  }

  render() {
    const post = this.props.data.mdx
    const comments = this.props.data.allCommentsJson

    return (
      <Layout>
        <Helmet>
          <title>{post.frontmatter.title} — Satchel</title>
        </Helmet>
        <div
          css={css`
            display: flex;
            @media only screen and (max-width: 576px) {
              flex-direction: column;
            }
          `}
        >
          <TableOfContents
            width={this.state.tocWidth}
            toc_data={post.tableOfContents}
          />
          <PopupSubscribeForm></PopupSubscribeForm>
          <div
            id="main_content"
            className={post.frontmatter.type}
            css={css`
              margin: 0 auto;
              margin-top: ${rhythm(1)};
              width: 100%;
              padding: 0 10px;
              margin-bottom: 150px;
              max-width: 620px;
            `}
          >
            <h1
              css={css`
                padding-top: 0;
                margin-bottom: 8px;
              `}
            >
              {post.frontmatter.title}
            </h1>

            {post.frontmatter.in_progress == "true" ? (
              <div
                css={css`
                  display: inline-block;
                  font-size: 14px;
                  font-weight: 600;
                  color: #473dc7;
                  background-color: #e0ddf6;
                  padding: 1px 5px;
                  border-radius: 3px;
                `}
              >
                In Progress
              </div>
            ) : (
              <div>
                <div
                  css={css`
                    font-size: 14px;
                    font-weight: 600;
                    // text-transform: uppercase;
                    color: #473dc7;

                    margin-bottom: 8px;
                  `}
                >
                  Last Updated {post.frontmatter.date}
                </div>

                <div
                  className="reading-time"
                  css={css`
                    margin-top: 20px;
                  `}
                >
                  Reading Times:
                  <div>Summary only: 1 minute</div>
                  <div>
                    Full guide: {accurateFullArticleTime(post.timeToRead)}{" "}
                    minutes
                  </div>
                </div>
              </div>
            )}

            {this.MaybeStubDisclaimer(post.frontmatter.stub)}

            <MDXProvider
              components={{
                Ratings,
                TrackedLink,
                PlaybookProduct,
                PlaybookCategory,
                PlaybookContext,
                PlaybookAlternatives,
                Recommendation,
              }}
            >
              <MDXRenderer>{post.body}</MDXRenderer>
            </MDXProvider>

            <SubscribeForm></SubscribeForm>

            <Comments slug={post.fields.slug} comments={comments}></Comments>
          </div>
        </div>
      </Layout>
    )
  }
}

export default PostTemplate

export const query = graphql`
  query($slug: String!) {
    mdx(fields: { slug: { eq: $slug } }) {
      body
      timeToRead
      tableOfContents(maxDepth: 3)
      fields {
        slug
      }
      frontmatter {
        title
        type
        date(formatString: "MMMM DD, YYYY")
        in_progress
        stub
      }
    }
    allCommentsJson(
      filter: { slug: { eq: $slug } }
      sort: { fields: [date], order: ASC }
    ) {
      edges {
        node {
          message
          name
          date
        }
      }
    }
  }
`
